import notifee, { TimestampTrigger, TriggerType } from '@notifee/react-native';
import { uniqueId } from 'lodash';
import { Platform } from "react-native";

/**
 * Schedules a notification using Notifee with a specific date and time.
 * @param title - The title of the notification.
 * @param body - The body content of the notification.
 * @param date - The date and time for the notification to trigger.
 * @param dependencies - Additional dependencies or metadata to include in the notification.
 * @param navigation - Navigation configuration for when the notification is pressed.
 *                     Example: { stack: 'Dashboard', screen: 'StatisticsScreen', params: {} }
 * @param reduxActions - Array of Redux actions to dispatch when the notification is pressed.
 *                       Example: [{ type: 'OPENED MENU' }, { type: 'SHOW MESSAGE MODAL' }]
 */
export async function scheduleNotification({
	id,
	title,
	body,
	date,
	dependencies,
	navigation,
	reduxActions,
}: {
	id: string;
	title: string;
	body: string;
	date: Date;
	dependencies?: Record<string, any>;
	navigation?: {
		stack: string;
		screen: string;
		params?: Record<string, any>;
	};
	reduxActions?: Array<{
		type: string;
		payload?: any;
	}>;
}): Promise<void> {
	try {
		//Check if id is already queued
		const existingNotifications = await notifee.getTriggerNotifications();
		const isAlreadyQueued = existingNotifications.some(
			(notification) => notification.notification.id === id
		);

		if (isAlreadyQueued) {
			console.log(`Notification with id ${id} is already queued.`);
			return;
		}

		const trigger: TimestampTrigger = {
			type: TriggerType.TIMESTAMP,
			timestamp: date ? date.getTime() : Date.now() + 3000, // Time in milliseconds
		};

		// Merge navigation info, Redux actions, and dependencies in notification data
		const notificationData = {
			...dependencies,
			...(navigation && {
				stack: navigation.stack,
				screen: navigation.screen,
				...(navigation.params && { params: navigation.params }),
			}),
			...(reduxActions &&
				reduxActions.length > 0 && {
					reduxActions: JSON.stringify(reduxActions), // Stringify for storage in notification data
				}),
		};

		if (Platform.OS === 'android') {
			await notifee.createTriggerNotification(
				{
					id,
					title,
					body,
					android: {
						channelId: 'default',
						pressAction: {
							id: 'default',
						},
						largeIcon: 'https://example.com/image.png', // URL or local image
					},
					data: notificationData, // Pass additional metadata including navigation info
				},
				trigger
			);
		} else {
			await notifee.createTriggerNotification(
				{
					id: id || uniqueId('notification'),
					title,
					body,
					ios: {
						attachments: [
							{
								url: 'https://example.com/image.png', // URL or local image
								thumbnailHidden: false,
							},
						],
					},
					data: notificationData, // Pass additional metadata including navigation info
				},
				trigger
			);
		}
	} catch (error) {
		console.error('Failed to schedule notification:', error);
	}
}